import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaCogs, FaChartBar, FaFlask, FaSpinner, FaEye, FaTimes } from 'react-icons/fa';
import api from '../../config/axios';
import { useTableContext } from '../data/TableManager/context/TableContext';
import { useTheme } from '../../contexts/ThemeContext';
import { logger } from '../../utils/logger';

const CustomActionButtons = () => {
  const { selectedRow, setError, setSuccess } = useTableContext();
  const { theme: currentThemeName } = useTheme();
  const safeTheme = React.useMemo(() => ({ themeColor: currentThemeName === 'dark' ? 'indigo' : 'emerald' }), [currentThemeName]);
  const themeColor = safeTheme.themeColor;

  const selectedRowId = selectedRow?.MAC_Id_MacroFunzioni;

  const [isCensimentoFunzioni, setIsCensimentoFunzioni] = useState(false);
  const [censimentoResult, setCensimentoResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  
  // Nuovo stato per debug delle validazioni
  const [validationDetails, setValidationDetails] = useState(null);

  // Riferimento per tracciare se il componente è montato
  const isMountedRef = useRef(true);

  // ✅ FIX: Simplified cleanup - only set to false on actual unmount
  useEffect(() => {
    // Ensure mounted state is true when component mounts
    isMountedRef.current = true;
    
    return () => {
      // Only set to false when component actually unmounts
      isMountedRef.current = false;
    };
  }, []); // Empty dependency array ensures this only runs on mount/unmount

  // Debug logging removed for cleaner console output
  const logTableContextDebug = useCallback(() => {
    // Debug removed
  }, [selectedRow, selectedRowId, setError, setSuccess]);

  useEffect(() => {
    logTableContextDebug();
  }, [logTableContextDebug]);

  // Funzione migliorata per validare i dati prima del censimento
  const validateCensimentoData = useCallback(() => {
    const details = {
      hasSelectedRow: !!selectedRow,
      selectedRowId,
      validations: {
        macroFunzioneId: {
          value: selectedRow?.MAC_Id_MacroFunzioni,
          valid: !!(selectedRow?.MAC_Id_MacroFunzioni)
        },
        moduloId: {
          value: selectedRow?.MAC_Id_Moduli,
          valid: !!(selectedRow?.MAC_Id_Moduli)
        },
        nome: {
          value: selectedRow?.MAC_Nome,
          valid: !!(selectedRow?.MAC_Nome)
        }
      },
      isMounted: isMountedRef.current,
      isCensimentoInProgress: isCensimentoFunzioni
    };
    
    setValidationDetails(details);
    logger.info('[CustomActionButtons] Validation details:', details);
    
    return details;
  }, [selectedRow, selectedRowId, isCensimentoFunzioni]);

  // ✅ FIX: Simplified function with better mount checking
  const handleCensisciFunzioni = useCallback(async () => {
    logger.info('[CustomActionButtons] ========== INIZIO CENSIMENTO FUNZIONI ==========');
    logger.info('[CustomActionButtons] isMountedRef.current:', isMountedRef.current);
    
    // ✅ FIX: Only check mounted state for critical operations, not at the start
    // Validazione preliminare estesa
    const validation = validateCensimentoData();

    if (!validation.hasSelectedRow || !selectedRowId) {
      const errorMsg = "Nessuna macro funzione selezionata per il censimento.";
      logger.warn('[CustomActionButtons] ' + errorMsg);
      if (setError) setError(errorMsg); 
      return;
    }

    // Validazione dettagliata dei campi obbligatori
    const missingFields = [];
    if (!validation.validations.macroFunzioneId.valid) missingFields.push('MAC_Id_MacroFunzioni');
    if (!validation.validations.moduloId.valid) missingFields.push('MAC_Id_Moduli');
    if (!validation.validations.nome.valid) missingFields.push('MAC_Nome');
    
    if (missingFields.length > 0) {
      const errorMsg = `Campi obbligatori mancanti: ${missingFields.join(', ')}`;
      logger.error('[CustomActionButtons] ' + errorMsg, { 
        missingFields, 
        selectedRow: selectedRow ? Object.keys(selectedRow) : null 
      });
      if (setError) setError(errorMsg);
      return;
    }

    if (isCensimentoFunzioni) {
      logger.warn('[CustomActionButtons] Censimento già in corso, ignoro la richiesta');
      return;
    }

    logger.info('[CustomActionButtons] Tutte le validazioni superate - inizio processo');

    setIsCensimentoFunzioni(true);
    if (setError) setError(null);
    if (setSuccess) setSuccess(null);
    setCensimentoResult(null);
    
    try {
      // Payload corretto secondo il controller generateFunctions
      const payload = { macroFunzioneRecord: selectedRow };
      logger.info('[CustomActionButtons] Payload per /censimento/funzioni:', {
        payloadKeys: Object.keys(payload.macroFunzioneRecord),
        macroFunzioneId: payload.macroFunzioneRecord.MAC_Id_MacroFunzioni,
        nome: payload.macroFunzioneRecord.MAC_Nome,
        modulo: payload.macroFunzioneRecord.MAC_Id_Moduli
      });
      
      logger.info('[CustomActionButtons] Invio richiesta POST a /censimento/funzioni...');
      const response = await api.post('/censimento/funzioni', payload);
      
      // ✅ FIX: Check mounted state only for state updates
      if (!isMountedRef.current) {
        logger.warn('[CustomActionButtons] Componente smontato durante la richiesta, ignoro la risposta');
        return;
      }
      
      logger.info('[CustomActionButtons] Risposta ricevuta da /censimento/funzioni:', {
        status: response.status,
        success: response.data?.success,
        message: response.data?.message,
        dataKeys: response.data?.data ? Object.keys(response.data.data) : null
      });

      if (response.data?.success) {
        const successMsg = response.data.message || "Funzioni censite con successo.";
        if (setSuccess) setSuccess(successMsg);
        setCensimentoResult({ 
          type: 'functions',
          data: response.data.data, 
          timestamp: new Date().toISOString() 
        });
        logger.info('[CustomActionButtons] Censimento completato con successo');
      } else {
        const errorMsg = response.data.message || "Errore durante il censimento delle funzioni.";
        if (setError) setError(errorMsg);
        logger.error('[CustomActionButtons] Errore risposta API:', errorMsg);
      }
    } catch (err) {
      if (!isMountedRef.current) {
        logger.warn('[CustomActionButtons] Componente smontato durante gestione errore');
        return;
      }
      
      logger.error("[CustomActionButtons] Errore API Censisci Funzioni:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        url: err.config?.url,
        method: err.config?.method
      });
      
      const errorMsg = err.response?.data?.message || err.message || "Errore di comunicazione con il server durante il censimento funzioni.";
      if (setError) setError(`Errore: ${errorMsg} (Status: ${err.response?.status || 'Unknown'})`);
    } finally {
      // ✅ FIX: Always reset loading state, even if component is unmounting
      setIsCensimentoFunzioni(false);
      logger.info('[CustomActionButtons] ========== FINE CENSIMENTO FUNZIONI ==========');
    }
  }, [selectedRowId, selectedRow, isCensimentoFunzioni, setError, setSuccess, validateCensimentoData]);

  const handleShowResults = useCallback(() => {
    if (censimentoResult && isMountedRef.current) {
      setShowResultModal(true);
    }
  }, [censimentoResult]);

  const renderResultDetails = () => {
    if (!censimentoResult || censimentoResult.type !== 'functions') return null;
    const { data } = censimentoResult;
    if (!data) return <p>Nessun dato dettagliato disponibile.</p>;

    return (
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Funzioni Censite/Gestite</h4>
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-sm">
          <p><strong>Messaggio:</strong> {data.message || "Operazione completata."}</p>
          <p><strong>Funzioni Create:</strong> {data.createdFunctions?.length || 0}</p>
          <p><strong>Funzioni Aggiornate (Versionate):</strong> {data.updatedFunctions?.length || 0}</p>
          <p><strong>ID Rif. Cancellati Fisicamente:</strong> {data.physicallyDeletedIdRifs?.length || 0}</p>
          
          {data.createdFunctions && data.createdFunctions.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer">Dettaglio Funzioni Create ({data.createdFunctions.length})</summary>
              <ul className="list-disc pl-5 mt-1 max-h-48 overflow-y-auto">
                {data.createdFunctions.map((func, idx) => (
                  <li key={idx}>
                    {func.functionName || func.FUN_Nome} (ID: {func.newFunctionId || func.FUN_Id_Funzioni})
                  </li>
                ))}
              </ul>
            </details>
          )}
          {data.updatedFunctions && data.updatedFunctions.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer">Dettaglio Funzioni Aggiornate ({data.updatedFunctions.length})</summary>
              <ul className="list-disc pl-5 mt-1 max-h-48 overflow-y-auto">
                {data.updatedFunctions.map((func, idx) => (
                  <li key={idx}>
                    {func.functionName || func.FUN_Nome} (Nuovo ID: {func.newId}, Vecchio ID: {func.oldId})
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    );
  };

  // Non renderizzo più il pannello di debug
  return (
    <>
      {showResultModal && censimentoResult && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className={`flex justify-between items-center px-6 py-4 bg-${themeColor}-100 dark:bg-${themeColor}-900 border-b border-gray-300 dark:border-gray-700`}>
              <h3 className={`text-lg font-semibold text-${themeColor}-800 dark:text-${themeColor}-100`}>
                Risultati Censimento Funzioni
              </h3>
              <button 
                onClick={() => setShowResultModal(false)}
                className={`text-${themeColor}-600 hover:text-${themeColor}-800 dark:text-${themeColor}-400 dark:hover:text-${themeColor}-200`}
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                <strong>Timestamp:</strong> {new Date(censimentoResult.timestamp).toLocaleString()}
              </div>
              {renderResultDetails()}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Dati Raw (Debug)</h4>
                <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-xs overflow-x-auto border border-gray-300 dark:border-gray-600">
                  {JSON.stringify(censimentoResult.data, null, 2)}
                </pre>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button 
                onClick={() => setShowResultModal(false)}
                className={`px-4 py-2 bg-${themeColor}-600 text-white rounded hover:bg-${themeColor}-700 transition`}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomActionButtons;